<script setup lang="ts">
import {
  type PickResult,
  type PointCloudHandle,
  type PointCloudPlugin,
  presets,
} from '@sharefe/share3d-engine'
import * as THREE from 'three'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef } from 'vue'
import { resolvePresetModelUrl } from '../examples/presetModels'
import { usePotreeEngine } from '../hooks'
import { resolvePointCloudInputPath } from '../utils/assetPath'
import {
  readPointCloudDemoUrlConfig,
  reloadPointCloudDemoConfig,
  type PointCloudDemoUrlPatch,
  type PointDrawMode,
} from '../utils/pointCloudDemoUrlConfig'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneAssetLoaderCard from './SceneAssetLoaderCard.vue'
import ScenePointBudgetCard from './ScenePointBudgetCard.vue'
import SceneControlPanel from './SceneControlPanel.vue'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'

interface SemanticClassItem {
  classification: number
  color: string
}

interface PickedSemanticPoint {
  assetId: string
  classification: number
  pointIndex?: number
  position: [number, number, number]
}

const DEFAULT_POINT_CLOUD_MODEL = resolvePresetModelUrl(
  '/semantic_cloud.las_converted/metadata.json',
)
const pointCloudDemoConfig = readPointCloudDemoUrlConfig()
const DEFAULT_SEMANTIC_COLOR = '#334155'
const DEFAULT_SEMANTIC_CLASSES: readonly SemanticClassItem[] = [
  { classification: 0, color: '#94a3b8' },
  { classification: 16, color: '#f8fafc' },
  { classification: 81, color: '#22c55e' },
  { classification: 82, color: '#eab308' },
  { classification: 83, color: '#ef4444' },
  { classification: 84, color: '#3b82f6' },
  { classification: 85, color: '#a855f7' },
]

const containerRef = useTemplateRef('containerRef')
const pointCloudInput = ref(DEFAULT_POINT_CLOUD_MODEL)
const pointCloudLoading = ref(false)
const pointCloudLoadError = ref('')
const semanticModeEnabled = ref(false)
const pointDrawMode = ref<PointDrawMode>(pointCloudDemoConfig.pointDrawMode)
const groupedDrawPointLimit = ref(pointCloudDemoConfig.groupedDrawPointLimit)
const drawCallColorEnabled = ref(false)
const pointCloudTotalPoints = ref(0)
const pointCloudVisiblePoints = ref(0)
const pointCloudVisibleNodes = ref(0)
const framesPerSecond = ref(0)
const frameTimeMs = ref(0)
const semanticClasses = ref(cloneDefaultSemanticClasses())
const currentHandle = shallowRef<PointCloudHandle | null>(null)
const pickedPoint = ref<PickedSemanticPoint | null>(null)
const pickNotice = ref('点选点云中的任意点，查看该点的 classification。')
const pickMarker = ref<{ x: number; y: number } | null>(null)

let pointerStart: { x: number; y: number } | null = null
let pickRequestId = 0
let pointCloudStatsTimer: number | null = null

const graphicsCard = useExampleGraphicsCard({ presetKey: 'pointCloudOnly' })

let fpsRafId = 0
let fpsWindowStartedAt = 0
let fpsWindowFrames = 0

function startFpsCounter() {
  fpsWindowStartedAt = performance.now()
  fpsWindowFrames = 0
  const sample = (now: number) => {
    fpsWindowFrames += 1
    const elapsed = now - fpsWindowStartedAt
    if (elapsed >= 500) {
      const fps = (fpsWindowFrames * 1000) / elapsed
      framesPerSecond.value = Math.round(fps * 10) / 10
      frameTimeMs.value = Math.round((1000 / Math.max(fps, 0.001)) * 100) / 100
      fpsWindowStartedAt = now
      fpsWindowFrames = 0
    }
    fpsRafId = window.requestAnimationFrame(sample)
  }
  fpsRafId = window.requestAnimationFrame(sample)
}

function stopFpsCounter() {
  if (fpsRafId !== 0) {
    window.cancelAnimationFrame(fpsRafId)
    fpsRafId = 0
  }
}

const { engine, init, actualBackend } = usePotreeEngine(containerRef, {
  backend: graphicsCard.requestedBackend.value,
  plugins: presets.pointCloudOnly(),
  renderMode: 'on-demand',
  background: '#05070b',
  pointBudget: pointCloudDemoConfig.pointBudget,
  controlMode: 'spacemouse',
})

const renderModeLabel = computed(() => (semanticModeEnabled.value ? '语义模式' : 'RGB 模式'))
const attributeNames = computed(() =>
  currentHandle.value?.attributes.attributes.map((attribute) => attribute.name).join(', '),
)

function cloneDefaultSemanticClasses(): SemanticClassItem[] {
  return DEFAULT_SEMANTIC_CLASSES.map((item) => ({ ...item }))
}

function createClassificationColorMap(): Record<number, string> {
  const mapping: Record<number, string> = {}
  for (const item of semanticClasses.value) {
    mapping[item.classification] = item.color
  }
  return mapping
}

function applySemanticColors() {
  engine.value?.pointClouds.setClassificationColorMap(
    createClassificationColorMap(),
    DEFAULT_SEMANTIC_COLOR,
  )
}

function setColorMode(mode: 'classification' | 'rgba') {
  semanticModeEnabled.value = mode === 'classification'
  currentHandle.value?.setColorMode(mode)
}

function updateSemanticColor(item: SemanticClassItem, event: Event) {
  const input = event.target as HTMLInputElement
  item.color = input.value
  applySemanticColors()
}

function resetSemanticColors() {
  semanticClasses.value = cloneDefaultSemanticClasses()
  applySemanticColors()
}

function clearPickResult() {
  pickedPoint.value = null
  pickMarker.value = null
  pickNotice.value = '点选点云中的任意点，查看该点的 classification。'
}

function setPointDrawMode(mode: PointDrawMode) {
  const plugin = engine.value?.plugins.get<PointCloudPlugin>('pointCloud')
  if (!plugin) return
  pointDrawMode.value = mode
  plugin.setConfig({
    renderStrategy: mode === 'grouped' ? 'batched-sprite' : 'default',
    batchPointBudget: groupedDrawPointLimit.value,
  })
}

function applyPointDrawModeFromUrl() {
  const configured = readPointCloudDemoUrlConfig()
  const mode = actualBackend.value === 'webgpu' ? configured.pointDrawMode : 'node'
  setPointDrawMode(mode)
}

function reloadPointCloudDemo(patch: PointCloudDemoUrlPatch) {
  reloadPointCloudDemoConfig(patch)
}

function setDrawCallColorEnabled(enabled: boolean) {
  drawCallColorEnabled.value = enabled
  engine.value?.plugins.get<PointCloudPlugin>('pointCloud')?.setConfig({
    groupedDrawCallColor: enabled,
  })
}

function syncPointCloudStats() {
  const handle = currentHandle.value
  pointCloudTotalPoints.value = handle?.totalPointCount ?? 0
  pointCloudVisiblePoints.value = handle?.visiblePointCount ?? 0
  pointCloudVisibleNodes.value = handle?.visibleNodeCount ?? 0
}

function clearPointCloudScene() {
  const handle = currentHandle.value
  const engineInstance = engine.value
  if (handle && engineInstance) {
    engineInstance.pointClouds.dispose(handle.id)
  }

  currentHandle.value = null
  pointCloudLoadError.value = ''
  clearPickResult()
}

async function loadPointCloud() {
  const engineInstance = engine.value
  const rawPath = pointCloudInput.value.trim()
  if (!engineInstance || pointCloudLoading.value) {
    return
  }
  if (!rawPath) {
    pointCloudLoadError.value = '请输入 metadata.json 地址，或选择包含该文件的 Potree V2 目录。'
    return
  }

  pointCloudLoading.value = true
  pointCloudLoadError.value = ''
  clearPointCloudScene()

  let loadedHandle: PointCloudHandle | null = null
  try {
    loadedHandle = await engineInstance.loadPointCloud({
      url: resolvePointCloudInputPath(rawPath),
      name: 'Semantic Point Cloud',
    })

    const hasClassification = loadedHandle.attributes.attributes.some(
      (attribute) => attribute.name === 'classification',
    )
    if (!hasClassification) {
      throw new Error('当前点云不包含 classification 属性，无法进入语义模式。')
    }

    currentHandle.value = loadedHandle
    loadedHandle.setColorMode(semanticModeEnabled.value ? 'classification' : 'rgba')
    engineInstance.fitToAssets([loadedHandle.id])
    syncPointCloudStats()
    pickNotice.value = '加载完成。单击点云中的点可读取 classification。'
  } catch (error) {
    if (loadedHandle) {
      engineInstance.pointClouds.dispose(loadedHandle.id)
    }
    currentHandle.value = null
    pointCloudLoadError.value = error instanceof Error ? error.message : '点云加载失败。'
  } finally {
    pointCloudLoading.value = false
  }
}

function handlePointerDown(event: PointerEvent) {
  if (event.button !== 0) {
    pointerStart = null
    return
  }
  pointerStart = { x: event.clientX, y: event.clientY }
}

function handlePointerUp(event: PointerEvent) {
  const start = pointerStart
  pointerStart = null
  if (!start || event.button !== 0) {
    return
  }

  const movement = Math.hypot(event.clientX - start.x, event.clientY - start.y)
  if (movement > 5) {
    return
  }

  pickSemanticPoint(event)
}

async function pickSemanticPoint(event: PointerEvent) {
  const engineInstance = engine.value
  const handle = currentHandle.value
  const container = containerRef.value
  if (!engineInstance || !handle || !container) {
    return
  }

  const rect = container.getBoundingClientRect()
  const screen = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top)
  const requestId = ++pickRequestId
  try {
    const results = await engineInstance.views.pickAtScreenAsync(
      engineInstance.views.primaryViewId,
      screen,
      {
        kinds: ['pointCloud'],
        assetIds: [handle.id],
        all: false,
        interactionType: 'click',
      },
    )
    if (requestId !== pickRequestId) return
    updatePickResult(results[0], screen)
  } catch (error) {
    if (requestId !== pickRequestId) return
    pickedPoint.value = null
    pickMarker.value = null
    pickNotice.value = error instanceof Error ? `拾取失败：${error.message}` : '拾取失败'
  }
}

function updatePickResult(result: PickResult | undefined, screen: THREE.Vector2) {
  if (!result) {
    pickedPoint.value = null
    pickMarker.value = null
    pickNotice.value = '当前位置未拾取到点，请靠近可见点重试。'
    return
  }

  const classification = result.attributes?.classification
  if (typeof classification !== 'number') {
    pickedPoint.value = null
    pickMarker.value = null
    pickNotice.value = '已命中点，但拾取结果中没有 classification。'
    return
  }

  pickedPoint.value = {
    assetId: result.assetId,
    classification,
    pointIndex: result.pointIndex,
    position: [result.point.x, result.point.y, result.point.z],
  }
  pickMarker.value = { x: screen.x, y: screen.y }
  pickNotice.value = `拾取成功，classification = ${classification}`
}

function formatCoordinate(value: number): string {
  return Number.isFinite(value) ? value.toFixed(3) : '-'
}

onMounted(async () => {
  await graphicsCard.runPreflight()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(actualBackend.value)
  applyPointDrawModeFromUrl()

  const container = containerRef.value
  container?.addEventListener('pointerdown', handlePointerDown, true)
  container?.addEventListener('pointerup', handlePointerUp, true)

  applySemanticColors()
  startFpsCounter()
  await loadPointCloud()
  pointCloudStatsTimer = window.setInterval(syncPointCloudStats, 500)
})

onBeforeUnmount(() => {
  stopFpsCounter()
  if (pointCloudStatsTimer !== null) {
    window.clearInterval(pointCloudStatsTimer)
    pointCloudStatsTimer = null
  }
  const container = containerRef.value
  container?.removeEventListener('pointerdown', handlePointerDown, true)
  container?.removeEventListener('pointerup', handlePointerUp, true)
})
</script>

<template>
  <div class="semantic-page">
    <div class="semantic-layout">
      <main class="viewport-shell">
        <div ref="containerRef" class="point-cloud-viewport"></div>

        <div class="viewport-guide">
          <span class="guide-kicker">SEMANTIC POINT CLOUD</span>
          <strong>{{ renderModeLabel }}</strong>
          <span>{{ pickNotice }}</span>
        </div>

        <div class="fps-badge" aria-live="off">
          <strong>{{ framesPerSecond.toFixed(1) }}</strong>
          <span>FPS</span>
          <em>{{ frameTimeMs.toFixed(2) }} ms</em>
        </div>

        <div
          v-if="pickMarker"
          class="pick-marker"
          :style="{ left: `${pickMarker.x}px`, top: `${pickMarker.y}px` }"
          aria-hidden="true"
        ></div>
      </main>

      <SceneControlPanel
        layout="sidebar"
        kicker="Potree V2"
        title="语义点云"
        :meta="renderModeLabel"
      >
        <SceneAssetLoaderCard
          v-model="pointCloudInput"
          title="加载语义点云"
          subtitle="选择包含 metadata.json 的 Potree V2 目录"
          badge="classification"
          hint="内置样例会自动加载；也可选择本地转换目录验证完整语义分类。"
          :loading="pointCloudLoading"
          :can-clear="currentHandle !== null"
          :directory-entry-names="['metadata.json']"
          directory-pick-label="选择 Potree V2 目录"
          load-label="加载点云"
          @submit-load="loadPointCloud"
          @clear-scene="clearPointCloudScene"
        >
          <template #options>
            <label class="path-field">
              <span>metadata.json 地址</span>
              <input v-model="pointCloudInput" class="panel-input" type="text" />
            </label>
          </template>
        </SceneAssetLoaderCard>

        <ScenePointBudgetCard
          :total-points="pointCloudTotalPoints"
          :visible-points="pointCloudVisiblePoints"
          :visible-node-count="pointCloudVisibleNodes"
          :budget="pointCloudDemoConfig.pointBudget"
          :draw-mode="pointDrawMode"
          :show-draw-mode="actualBackend === 'webgpu'"
          :grouped-draw-point-limit="groupedDrawPointLimit"
          :show-grouped-draw-point-limit="actualBackend === 'webgpu' && pointDrawMode === 'grouped'"
          :draw-call-color-enabled="drawCallColorEnabled"
          :show-draw-call-color="actualBackend === 'webgpu' && pointDrawMode === 'grouped'"
          @reload-config="reloadPointCloudDemo"
          @toggle-draw-call-color="setDrawCallColorEnabled"
        />

        <GraphicsBackendControlCard :card="graphicsCard" />

        <details class="panel-section" open>
          <summary class="panel-section-summary">
            <span class="summary-main">
              <span class="panel-section-title">显示模式与服务级配色</span>
              <span class="panel-section-subtitle">颜色由服务继承，模式由当前点云控制</span>
            </span>
            <span class="summary-side">
              <span class="summary-count">{{ renderModeLabel }}</span>
              <span class="summary-chevron" aria-hidden="true">v</span>
            </span>
          </summary>

          <div class="panel-section-body semantic-mode-card">
            <div class="button-grid">
              <button
                class="panel-button"
                :class="{ active: semanticModeEnabled }"
                type="button"
                :disabled="!currentHandle"
                @click="setColorMode('classification')"
              >
                语义模式
              </button>
              <button
                class="panel-button"
                :class="{ active: !semanticModeEnabled }"
                type="button"
                :disabled="!currentHandle"
                @click="setColorMode('rgba')"
              >
                RGB 模式
              </button>
            </div>

            <div class="semantic-palette">
              <label
                v-for="item in semanticClasses"
                :key="item.classification"
                class="semantic-color-row"
              >
                <input
                  class="semantic-color-input"
                  type="color"
                  :value="item.color"
                  @input="updateSemanticColor(item, $event)"
                />
                <span>classification {{ item.classification }}</span>
                <code>{{ item.color }}</code>
              </label>
            </div>

            <button
              class="panel-button panel-button-subtle"
              type="button"
              @click="resetSemanticColors"
            >
              恢复示例配色
            </button>
          </div>
        </details>

        <details class="panel-section" open>
          <summary class="panel-section-summary">
            <span class="summary-main">
              <span class="panel-section-title">点拾取结果</span>
              <span class="panel-section-subtitle">返回 attributes.classification</span>
            </span>
            <span class="summary-side">
              <span class="summary-count">{{ pickedPoint ? '已命中' : '等待拾取' }}</span>
              <span class="summary-chevron" aria-hidden="true">v</span>
            </span>
          </summary>

          <div class="panel-section-body pick-result-card">
            <p v-if="pointCloudLoadError" class="error-message">{{ pointCloudLoadError }}</p>
            <p v-else class="pick-notice">{{ pickNotice }}</p>

            <div v-if="pickedPoint" class="classification-result">
              <span>classification</span>
              <strong>{{ pickedPoint.classification }}</strong>
            </div>

            <dl v-if="pickedPoint" class="point-details">
              <div>
                <dt>资产 ID</dt>
                <dd>{{ pickedPoint.assetId }}</dd>
              </div>
              <div v-if="pickedPoint.pointIndex !== undefined">
                <dt>点索引</dt>
                <dd>{{ pickedPoint.pointIndex }}</dd>
              </div>
              <div>
                <dt>坐标 X / Y / Z</dt>
                <dd>
                  {{ pickedPoint.position.map(formatCoordinate).join(' / ') }}
                </dd>
              </div>
            </dl>

            <p v-if="currentHandle" class="attribute-list">
              可用属性：{{ attributeNames || '无' }}
            </p>
          </div>
        </details>
      </SceneControlPanel>
    </div>
  </div>
</template>

<style scoped>
.semantic-page,
.semantic-layout {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.semantic-layout {
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(320px, 3fr);
  background: #05070b;
}

.viewport-shell,
.point-cloud-viewport {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.viewport-shell {
  overflow: hidden;
}

.fps-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(6, 14, 24, 0.78);
  border: 1px solid rgba(120, 156, 200, 0.28);
  color: #dff0ff;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
  z-index: 5;
}

.fps-badge strong {
  font-size: 16px;
  font-weight: 700;
}

.fps-badge span {
  font-size: 11px;
  letter-spacing: 0.08em;
  color: #9fb7c8;
}

.fps-badge em {
  font-style: normal;
  font-size: 11px;
  color: #86a2b6;
}

.viewport-guide {
  position: absolute;
  top: 20px;
  left: 20px;
  display: grid;
  gap: 5px;
  max-width: min(430px, calc(100% - 40px));
  padding: 14px 16px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 14px;
  background: rgba(7, 11, 18, 0.78);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.24);
  color: #e2e8f0;
  pointer-events: none;
  backdrop-filter: blur(12px);
}

.viewport-guide strong {
  font-size: 19px;
}

.viewport-guide span:last-child {
  font-size: 12px;
  line-height: 1.5;
  color: #a8b6c8;
}

.guide-kicker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: #5eead4;
}

.pick-marker {
  position: absolute;
  width: 22px;
  height: 22px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  box-shadow:
    0 0 0 4px rgba(45, 212, 191, 0.42),
    0 0 18px rgba(45, 212, 191, 0.88);
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.path-field {
  display: grid;
  gap: 7px;
}

.path-field > span {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #9fb5c7;
}

.semantic-mode-card,
.pick-result-card {
  display: grid;
  gap: 12px;
}

.semantic-palette {
  display: grid;
  gap: 7px;
}

.semantic-color-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 6px 9px;
  border: 1px solid rgba(148, 163, 184, 0.11);
  border-radius: 11px;
  background: rgba(3, 7, 18, 0.34);
  color: #dce7f2;
  font-size: 12px;
}

.semantic-color-row code {
  color: #91a6b8;
  font-size: 11px;
}

.semantic-color-input {
  width: 34px;
  height: 26px;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 7px;
  background: transparent;
  cursor: pointer;
}

.pick-notice,
.error-message,
.attribute-list {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
}

.pick-notice,
.attribute-list {
  color: #9fb5c7;
}

.error-message {
  padding: 10px 12px;
  border: 1px solid rgba(248, 113, 113, 0.3);
  border-radius: 11px;
  background: rgba(127, 29, 29, 0.18);
  color: #fecaca;
}

.classification-result {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 12px 14px;
  border: 1px solid rgba(45, 212, 191, 0.3);
  border-radius: 13px;
  background: linear-gradient(135deg, rgba(13, 84, 78, 0.42), rgba(13, 38, 52, 0.56));
}

.classification-result span {
  color: #99f6e4;
  font-size: 12px;
}

.classification-result strong {
  color: #f0fdfa;
  font-size: 30px;
  line-height: 1;
}

.point-details {
  display: grid;
  gap: 8px;
  margin: 0;
}

.point-details div {
  display: grid;
  gap: 3px;
}

.point-details dt {
  color: #7f96aa;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.point-details dd {
  margin: 0;
  overflow-wrap: anywhere;
  color: #e5edf5;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
}

.attribute-list {
  padding-top: 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
  overflow-wrap: anywhere;
}

@media (max-width: 900px) {
  .semantic-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .viewport-guide {
    top: 12px;
    left: 12px;
    max-width: calc(100% - 76px);
  }

  .fps-badge {
    top: 12px;
    right: 12px;
  }
}
</style>
